import { formatPrice, orderFlowPriceFormatOptions } from '@/_lib/utils'
import { aggregateCartLines } from '@cart/_utils/cart-price-aggregates'


import type { CartVariant } from './a-cart-ui-primitives'
import type { PriceInfo } from '@cart/_hooks/use-cart-prices'

type CartSummarySectionProps = {
	productPrices: Record<string, PriceInfo>
	variant: CartVariant
	/** Uses alternate discount copy for family bundle checkout. @default false */
	isFamily?: boolean
	/**
	 * When true the patient has not yet selected an insurer and the final price is unknown.
	 * Replaces the "Na zaplatenie" price row with an explanatory message instead.
	 * Only relevant for DEFAULT / PLUS_WITH ambulance types; never set for PLUS_DEFAULT_PRICE_FOR_INSURED.
	 */
	insurancePending?: boolean
}

/**
 * Footer block: optional "Cena" + discount rows, then "Na zaplatenie" with formatted totals.
 *
 * When `insurancePending` is true (DEFAULT / PLUS_WITH types, no insurer selected yet),
 * the final-price row is replaced with a note that the price will be determined once
 * the patient picks an insurer.
 */
export function CartSummarySection({
	productPrices,
	variant,
	isFamily = false,
	insurancePending = false
}: CartSummarySectionProps) {
	const { totalAll, savingsAll } = aggregateCartLines(productPrices)
	const originalAll = totalAll + savingsAll

	const fFinal = formatPrice(totalAll, orderFlowPriceFormatOptions)
	const fOriginal = formatPrice(originalAll, orderFlowPriceFormatOptions)
	const fSavings = formatPrice(savingsAll, orderFlowPriceFormatOptions)
	const discountLabel = isFamily ? 'Rodinná 10% zľava' : 'Zľava'

	if (variant === 'compact') {
		return (
			<div className='space-y-0.5 border-t-2 border-medante-purple-dark/20 pt-2'>
				{insurancePending ? (
					<p className='text-xs font-medium text-medante-purple-dark/60'>
						Finálna cena závisí od zvolenej poisťovne
					</p>
				) : (
					<>
						{savingsAll > 0 && (
							<>
								<div className='flex items-baseline justify-between gap-4'>
									<p className='text-xs font-semibold text-medante-purple-dark/80'>
										Cena
									</p>
									<p className='text-xs font-medium text-medante-purple-dark/80'>
										{fOriginal.price}&nbsp;{fOriginal.currency}
									</p>
								</div>
								<div className='flex items-baseline justify-between gap-4'>
									<p className='text-xs font-medium text-medante-purple-dark/80'>
										{discountLabel}
									</p>
									<p className='text-xs font-medium text-medante-pink-secondary'>
										-{fSavings.price}&nbsp;{fSavings.currency}
									</p>
								</div>
							</>
						)}
						<div className='flex items-baseline justify-between gap-4'>
							<p className='text-xs font-semibold text-medante-purple-dark'>
								Na zaplatenie
							</p>
							<p className='text-xs font-semibold text-medante-purple-dark'>
								{fFinal.price}&nbsp;{fFinal.currency}
							</p>
						</div>
					</>
				)}
			</div>
		)
	}

	return (
		<div className='space-y-1.5 border-t border-medante-purple-dark/50 pt-4'>
			{insurancePending ? (
				<div className='h-[24px] md:h-[28px] flex items-center text-center'>
					<p className='text-xs md:text-sm font-medium text-medante-purple-dark/50 w-full'>
						Koncová cena sa vypočíta po zvolení poisťovne
					</p>
				</div>
			) : (
				<>
					{savingsAll > 0 && (
						<>
							<div className='flex items-baseline justify-between gap-4'>
								<p className='text-sm font-medium text-medante-purple-dark/100'>
									Cena
								</p>
								<p className='text-sm font-medium text-medante-purple-dark/100'>
									{fOriginal.price}&nbsp;{fOriginal.currency}
								</p>
							</div>
							<div className='flex items-baseline justify-between gap-4'>
								<p className='text-sm font-medium text-medante-purple-dark/100'>
									{discountLabel}
								</p>
								<p className='text-sm font-medium text-medante-pink-secondary'>
									-{fSavings.price}&nbsp;{fSavings.currency}
								</p>
							</div>
						</>
					)}
					<div className='flex items-baseline justify-between gap-4'>
						<p className='text-base lg:text-xl font-semibold text-medante-purple-dark'>
							Na zaplatenie
						</p>
						<p className='text-base lg:text-xl font-semibold text-medante-purple-dark'>
							{fFinal.price}&nbsp;{fFinal.currency}
						</p>
					</div>
				</>
			)}
		</div>
	)
}
